
import { useMemo, useState } from "react";
import { useCvBuilder } from "@/stores/cvBuilderStore";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { CvField } from './CvDataFields';

const CandidateStep = () => {
    const { draft, setCandidateField } = useCvBuilder();
    const { candidate } = draft;
    const [touched, setTouched] = useState({});

    const errors = useMemo(() => ({
        full_name: candidate.full_name?.trim() ? "" : "Renseignez le nom complet du candidat.",
        title: candidate.title?.trim() ? "" : "Renseignez le titre professionnel du candidat.",
    }), [candidate.full_name, candidate.title]);
    const visibleErrors = Object.entries(errors).filter(([field, message]) => touched[field] && message);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCandidateField(
            name,
            name === "years_of_experience"
                ? (value === "" || value === "0" || value === 0 ? null : (parseInt(value, 10) > 0 ? parseInt(value, 10) : null))
                : value
        );
    };

    const handleBlur = (event) => {
        setTouched((current) => ({ ...current, [event.target.name]: true }));
    };

    return (
        <div className="space-y-8" data-testid="candidate-step">
            {visibleErrors.length > 0 ? (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4" role="alert" aria-labelledby="candidate-errors-title">
                    <h3 id="candidate-errors-title" className="font-semibold text-rose-200">Vérifiez les informations essentielles</h3>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-rose-100">
                        {visibleErrors.map(([field, message]) => <li key={field}><a className="underline underline-offset-2" href={`#${field}`}>{message}</a></li>)}
                    </ul>
                </div>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-slate-300">Nom complet</Label>
                    <Input
                        id="full_name" data-cv-path="candidate.full_name"
                        name="full_name"
                        placeholder="Ex: Jean Dupont"
                        value={candidate.full_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        aria-invalid={Boolean(touched.full_name && errors.full_name)}
                        aria-describedby={touched.full_name && errors.full_name ? "full_name-error" : undefined}
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                    />
                    {touched.full_name && errors.full_name ? <p id="full_name-error" className="text-sm text-rose-300">{errors.full_name}</p> : null}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="location" className="text-slate-300">Localisation</Label>
                    <Input
                        id="location" data-cv-path="candidate.location"
                        name="location"
                        placeholder="Ex: Paris, France"
                        value={candidate.location}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-300">Email (Contact interne)</Label>
                    <Input
                        id="email" data-cv-path="candidate.email"
                        name="email"
                        type="email"
                        placeholder="Ex: jean.dupont@email.com"
                        value={candidate.email || ""}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-300">Téléphone (Contact interne)</Label>
                    <Input
                        id="phone" data-cv-path="candidate.phone"
                        name="phone"
                        placeholder="Ex: 06 12 34 56 78"
                        value={candidate.phone || ""}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label htmlFor="title" className="text-slate-300">Titre du poste</Label>
                    <Input
                        id="title" data-cv-path="candidate.title"
                        name="title"
                        placeholder="Ex: Architecte Cloud Senior"
                        value={candidate.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        aria-invalid={Boolean(touched.title && errors.title)}
                        aria-describedby={touched.title && errors.title ? "title-error" : undefined}
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                    />
                    {touched.title && errors.title ? <p id="title-error" className="text-sm text-rose-300">{errors.title}</p> : null}
                </div>
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label htmlFor="years_of_experience" className="text-slate-300">Années d'expérience</Label>
                        {candidate.years_of_experience === null && candidate.computed_years_of_experience != null && (
                            <span className="text-xs text-lenvia-blue/80 font-mono">Calculé ({candidate.computed_years_of_experience} ans)</span>
                        )}
                    </div>
                    <Input
                        id="years_of_experience" data-cv-path="candidate.years_of_experience"
                        name="years_of_experience"
                        type="number"
                        min="0"
                        value={candidate.years_of_experience ?? candidate.computed_years_of_experience ?? ""}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                    />
                    {candidate.years_of_experience === null && candidate.computed_years_of_experience != null && (
                        <p className="text-xs text-slate-400">Valeur calculée à partir de l'union des expériences. Modifiable à tout moment.</p>
                    )}
                </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor="subtitle" className="text-slate-300">Sous-titre / Accroche</Label>
                <Input
                    id="subtitle" data-cv-path="candidate.subtitle"
                    name="subtitle"
                    placeholder="Ex: Expert AWS & Kubernetes"
                    value={candidate.subtitle}
                    onChange={handleChange}
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                />
            </div>

            <CvField path="candidate.links" label="Liens professionnels — un par ligne" value={candidate.links} list
                onChange={value => setCandidateField('links', value)} />
            {candidate.computed_years_of_experience != null && <p data-cv-path="candidate.computed_years_of_experience" className="text-sm text-slate-400">
                Ancienneté calculée depuis le parcours : {candidate.computed_years_of_experience} ans. Ce calcul est distinct de l’ancienneté indiquée dans le CV.
            </p>}
            <div className="space-y-2">
                {!candidate.summary?.trim() && <p className="text-sm text-slate-400">Aucun résumé professionnel enregistré. Ce champ est facultatif et ne sera pas affiché dans le CV exporté tant qu’il reste vide. Les contextes de mission sont conservés dans les expériences et les projets.</p>}
                <Label htmlFor="summary" className="text-slate-300">Résumé professionnel</Label>
                <Textarea
                    id="summary" data-cv-path="candidate.summary"
                    name="summary"
                    rows={5}
                    placeholder="Résumé facultatif — ajoutez uniquement des informations vérifiées"
                    value={candidate.summary}
                    onChange={handleChange}
                    className="resize-none bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                />
            </div>
        </div>
    );
};

export default CandidateStep;
